import PremiumOverlayCard from '@hellodarwin/app-core/lib/layouts/premium-access/components/premium-paywall-overlay/premium-overlay-card';
import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers/premium-access-provider';
import Button from '@hellodarwin/core/lib/components/common/button';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import Loading from '@hellodarwin/core/lib/components/loading';
import { ProgramTargetUser } from '@hellodarwin/core/lib/features/entities/programs-entities';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/app-hooks';
import {
  GetProgram,
  fetchGinApplication,
  selectGinApplicationById,
} from '../../../features/api/slices/programs-slice';
import { useClientApi } from '../../../features/api/use-client-api';
import ClientSingleApplication from './v2';
import {
  ClientSingleApplicationPageContainer,
  ClientSingleApplicationPageLoadError,
} from './styles';

const ClientSingleApplicationPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const legacyApi = useClientApi();
  const { selectedLocale } = useLocale();
  const { programId } = useParams();
  const [viewMode, setViewMode] = useState<'v2' | undefined>(undefined);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const { t } = useTranslations();
  const { isSectionAccessible } = usePremiumAccess();
  const isAccessible = isSectionAccessible('premium');
  const application = useAppSelector((state) =>
    selectGinApplicationById(state, programId || ''),
  );
  const canAccessApplication =
    isAccessible ||
    application?.application_target_user === ProgramTargetUser.Admin;

  useEffect(() => {
    let cancelled = false;
    setViewMode(undefined);
    setLoadFailed(false);
    if (!programId) return;
    Promise.all([
      dispatch(
        fetchGinApplication({ api: legacyApi, applicationId: programId }),
      ).unwrap(),
      dispatch(
        GetProgram({ api: legacyApi, programId, locale: selectedLocale }),
      ).unwrap(),
    ])
      .then(() => {
        if (!cancelled) setViewMode(getViewMode());
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, legacyApi, loadAttempt, programId, selectedLocale]);

  const getViewMode = (): 'v2' | undefined => {
    return 'v2';
  };

  const handlePremiumOverlayClose = () => {
    if (window.history.length > 2 && window.location.hash) {
      navigate(-2);
    } else {
      navigate('/dashboard');
    }
  };

  if (loadFailed) {
    return (
      <ClientSingleApplicationPageLoadError role="alert">
        <Typography>{t('program-workspace|applicationLoadError')}</Typography>
        <Button variant="outline" onClick={() => setLoadAttempt((value) => value + 1)}>
          {t('program-workspace|applicationLoadRetry')}
        </Button>
      </ClientSingleApplicationPageLoadError>
    );
  }

  if (viewMode == null) return <Loading />;

  return (
    <>
      <div style={{ filter: !canAccessApplication ? 'blur(5px)' : undefined }}>
        <ClientSingleApplication />
      </div>

      {!canAccessApplication && (
        <ClientSingleApplicationPageContainer>
          <PremiumOverlayCard onClose={handlePremiumOverlayClose} />
        </ClientSingleApplicationPageContainer>
      )}
    </>
  );
};

export default ClientSingleApplicationPage;
